/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import MarketTotalRevenueTooltip from 'lumen/components/market/total-revenue/tooltip';
import type { TotalRevenueTrend, TotalRevenueTrendItem } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import { getHighchartsDate } from 'lumen/utils/charts/chart-utils';
import { CHART_INTERVALS } from 'lumen/utils/constants/general-constants';
import {
  type Metric,
  METRIC_PAR_TRANSLATIONS,
  METRIC_TRANSLATIONS,
} from 'lumen/utils/constants/total-revenue-constants';

interface ReferenceValueConfig {
  data: { x: number; y: number | null }[];
  id: string;
  marker: {
    enabled: boolean;
  };
  name: string;
  type: 'line';
}

interface ReferenceValuesConfigObject {
  [key: string]: ReferenceValueConfig;
}

interface MarketTotalRevenueChartTrendSignature {
  Args: {
    aggregation: 'day' | 'week' | 'month';
    chartSelectionChanged: (startDate: Dayjs, endDate: Dayjs) => void;
    data: TotalRevenueTrend | null | undefined;
    isLoading?: boolean;
    isPerAvailableRoom: boolean;
    metric: Metric;
  };
}

export default class MarketTotalRevenueChartTrend extends Component<MarketTotalRevenueChartTrendSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked hoveredPoint?: TotalRevenueTrendItem;

  declare tooltipElement: HTMLElement;

  declare warningElement: HTMLElement;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get aggregatedData() {
    if (this.args.isLoading || !this.args.data) {
      return [];
    }

    switch (this.args.aggregation) {
      case 'week':
        return this.args.data.weeksSorted;
      case 'month':
        return this.args.data.monthsSorted;
      default:
        return this.args.data.daysSorted;
    }
  }

  /**
   * Memoized aggregated data by X value.
   * This is used to quickly find the hovered point in the chart.
   */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get aggregatedDataByXValue() {
    return this.aggregatedData.reduce<Record<number, TotalRevenueTrendItem>>(
      (acc, item) => {
        const xValue = this.getXValueForDate(item.date);
        acc[xValue] = item;
        return acc;
      },
      {},
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const totalRevenueSeriesConfig = this.generateLineSeriesConfig(
      'total-revenue',
      this.args.isPerAvailableRoom
        ? this.intl.t('general.trevpar')
        : this.intl.t('total_revenue.total_revenue'),
    );

    const referenceValues = this.aggregatedData[0]?.sortedReferenceValues ?? [];

    const referenceValuesConfigObject = referenceValues.reduce((acc, item) => {
      acc[item.referenceType] = this.generateLineSeriesConfig(
        item.referenceType,
        item.translatedReferenceType,
      );

      return acc;
    }, {} as ReferenceValuesConfigObject);

    this.aggregatedData.forEach((item) => {
      const xValue = this.getXValueForDate(item.date);

      totalRevenueSeriesConfig.data.push({
        x: xValue,
        y: item[this.args.metric],
      });

      item.referenceValues.forEach((referenceValue) => {
        referenceValuesConfigObject[referenceValue.referenceType]?.data.push({
          x: xValue,
          y: referenceValue[this.args.metric],
        });
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return [
      totalRevenueSeriesConfig,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ...Object.values(referenceValuesConfigObject as object),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return {
      chart: {
        type: 'line',
        height: 280, // Must match `.chart-trend` height in `total-revenue.css`
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          click() {
            if (self.hoveredPoint) {
              self.updateChartSelection(
                self.hoveredPoint.date,
                self.hoveredPoint.date,
              );
            }
          },
        },
      },
      xAxis: {
        type: 'datetime' as const,
        minTickInterval: CHART_INTERVALS[this.args.aggregation],
        labels: {
          useHTML: true,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = this.value as number;

            const formattedDate = getHighchartsDate(
              self.intl,
              self.dates,
              value,
              self.args.aggregation,
            );

            const warningIconHTML = self.warningElement.innerHTML;

            const firstPoint = self.aggregatedData[0];
            const isFirstPointAggregationIncomplete =
              firstPoint && self.isItemAggregationIncomplete(firstPoint);

            const lastPoint =
              self.aggregatedData[self.aggregatedData.length - 1];
            const isLastPointAggregationIncomplete =
              lastPoint && self.isItemAggregationIncomplete(lastPoint);

            const showWarningSign =
              (this.isFirst && isFirstPointAggregationIncomplete) ||
              (this.isLast && isLastPointAggregationIncomplete);

            const optionalWarningIcon = showWarningSign ? warningIconHTML : '';

            return [
              `<div class="x-axis-label-with-icon">`,
              `  <div>${formattedDate}</div>`,
              `  ${optionalWarningIcon}`,
              `</div>`,
            ].join('\n');
          },
        },
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject) {
            if (e.trigger === 'zoom') {
              // Add 12 hours to prevent wrong conversion from x value (continuous var) to date
              const dayjsStart = dayjs.utc(e.userMin).add(12, 'hours');
              const dayjsEnd = dayjs.utc(e.userMax).add(12, 'hours');

              self.updateChartSelection(dayjsStart, dayjsEnd);
            }
          },
        },
      },
      yAxis: {
        title: {
          text: undefined,
        },
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        borderWidth: 0,
        shadow: false,
        padding: 0,
        outside: true,
        useHTML: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          begin();
          self.hoveredPoint = self.aggregatedDataByXValue[this.x];
          end();

          return self.tooltipElement.innerHTML;
        },
      },
      plotOptions: {
        series: {
          events: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click() {
              if (self.hoveredPoint) {
                self.updateChartSelection(
                  self.hoveredPoint.date,
                  self.hoveredPoint.date,
                );
              }
            },
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartTitle() {
    const translationMappingToUse = this.args.isPerAvailableRoom
      ? METRIC_PAR_TRANSLATIONS
      : METRIC_TRANSLATIONS;

    return this.intl.t(translationMappingToUse[this.args.metric]);
  }

  generateLineSeriesConfig(id: string, name: string): ReferenceValueConfig {
    return {
      id,
      name,
      type: 'line',
      data: [],
      marker: {
        enabled: true,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getXValueForDate(date: Dayjs) {
    return this.hotelAndMarketService.toLocalUnixTime(date.unix());
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isItemAggregationIncomplete(item: TotalRevenueTrendItem) {
    if (
      this.args.isLoading ||
      this.args.aggregation === 'day' ||
      !this.args.data
    ) {
      return false;
    }

    const startDate = this.args.data.daysSorted[0]?.date;
    const endDate =
      this.args.data.daysSorted[this.args.data.daysSorted.length - 1]?.date;
    if (!startDate || !endDate) {
      return false;
    }

    const startOfAggregation = item.date;
    const endOfAggregation = item.date.endOf(this.args.aggregation);

    return (
      startOfAggregation.isBefore(startDate, 'day') ||
      endOfAggregation.isAfter(endDate, 'day')
    );
  }

  @action setTooltipElement(element: HTMLElement): void {
    this.tooltipElement = element;
  }

  @action setWarningIconElement(element: HTMLElement): void {
    this.warningElement = element;
  }

  @action updateChartSelection(startDate: Dayjs, endDate: Dayjs): void {
    const startOfAggregation = startDate.startOf(this.args.aggregation);
    const endOfAggregation = endDate.endOf(this.args.aggregation);

    this.args.chartSelectionChanged(startOfAggregation, endOfAggregation);
  }

  <template>
    <PrismCard>
      <:header as |Header|>
        {{#if @isLoading}}
          <div class="loading-pulse mt-200 mb-200 w-1/2"></div>
        {{else}}
          <Header.title>
            {{this.chartTitle}}
          </Header.title>
        {{/if}}
      </:header>

      <:content>
        <div class="chart-trend">
          {{#unless @isLoading}}
            <GeneralChartsOtaChart
              @chartOptions={{this.chartOptions}}
              @model={{this.chartData}}
            />
          {{/unless}}
        </div>
      </:content>
    </PrismCard>

    <div {{getElement this.setTooltipElement}} hidden>
      {{#if this.hoveredPoint}}
        <MarketTotalRevenueTooltip
          @aggregation={{@aggregation}}
          @isIncompleteAggregation={{this.isItemAggregationIncomplete
            this.hoveredPoint
          }}
          @isPerAvailableRoom={{@isPerAvailableRoom}}
          @metric={{@metric}}
          @trendItem={{this.hoveredPoint}}
        />
      {{/if}}
    </div>

    <div {{getElement this.setWarningIconElement}} hidden>
      <PrismIcon @color="warning" @name="warning" @size="extra-small" />
    </div>
  </template>
}
